import { FiPlus, FiCheck, FiChevronRight, FiChevronLeft, FiCalendar, FiEdit2, FiX } from 'react-icons/fi';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../supabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { startOfMonth, endOfMonth, eachDayOfInterval, format, isSameMonth, isSameDay, addMonths, subMonths, parseISO, isBefore, startOfDay } from 'date-fns';
import { getHolidays } from '../../utils/holidays';
import { getLocalDateISOString, getLocalDateTimeString } from '../../utils/dateUtils';
import { toast } from 'sonner';
import QuickPostModal from '../engagement/QuickPostModal';
import EditPostModal from '../social/EditPostModal';
import { googleCalendarService } from '../../services/googleCalendarService';


const parseEventDate = (dateStr) => {
  if (!dateStr) return new Date();
  if (dateStr instanceof Date) return dateStr;

  // Only use manual parsing for simple YYYY-MM-DD strings (length 10)
  // This prevents incorrect timezone interpretation for ISO strings (which should use new Date())
  if (typeof dateStr === 'string' && dateStr.length === 10) {
      const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (dateOnlyRegex.test(dateStr)) {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(year, month - 1, day);
      }
  }
  return new Date(dateStr);
};

const formatForDatetimeLocal = (dateStr) => {
    if (!dateStr) return '';
    const date = parseEventDate(dateStr);

    if (typeof dateStr === 'string' && dateStr.length === 10) {
        return format(date, 'yyyy-MM-dd') + 'T12:00';
    }

    return format(date, "yyyy-MM-dd'T'HH:mm");
};

const MarketingCalendar = ({ showHolidays = false, highlightEventId = null }) => {
  const { activeWorkspace } = useWorkspace();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('month'); // 'month', 'list'

  // Modal States
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedSocialPost, setSelectedSocialPost] = useState(null);

  const [selectedEvent, setSelectedEvent] = useState(null);


  // Day Detail Modal State
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [selectedDayDate, setSelectedDayDate] = useState(null);

  // Edit Form State
  const [editForm, setEditForm] = useState({
      title: '',
      event_date: '',
      type: '',
      description: '',
      location: ''
  });

  const [newEvent, setNewEvent] = useState({
      title: '',
      event_date: getLocalDateTimeString(),
      type: 'campaign',
      description: '',
      location: ''
  });

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    if (activeWorkspace) {
      fetchEvents();
    }
  }, [activeWorkspace, currentDate]);

  const fetchEvents = async () => {
    try {
      setLoading(true);

      // If viewMode is list, we might want to fetch more data, but for now we'll fetch the current month's data
      // or maybe a 3-month window. Let's fetch the current month + next month for list view.
      const start = startOfMonth(currentDate).toISOString();
      const end = viewMode === 'list'
        ? endOfMonth(addMonths(currentDate, 2)).toISOString()
        : endOfMonth(currentDate).toISOString();

      // Fetch data concurrently for better performance
      const [eventsResponse, emailResponse, gcalResponse, flywheelResponse, socialPostsResponse] = await Promise.all([
        supabase
          .from('marketing_events')
          .select('*')
          .eq('workspace_id', activeWorkspace.id)
          .gte('event_date', start)
          .lte('event_date', end),
        supabase
          .from('email_campaigns')
          .select('id, name, subject, scheduled_at, status')
          .eq('workspace_id', activeWorkspace.id)
          .gte('scheduled_at', start)
          .lte('scheduled_at', end),
        googleCalendarService.listEvents(activeWorkspace.id, start, end).catch(err => {
            console.log("Google Calendar events not fetched:", err.message);
            return [];
        }),
        supabase
          .from('events')
          .select('id, name, type, launch_date, status')
          .eq('workspace_id', activeWorkspace.id)
          .gte('launch_date', start)
          .lte('launch_date', end),
        supabase
          .from('social_media_posts')
          .select('*')
          .eq('workspace_id', activeWorkspace.id)
          .eq('is_event', true)
          .gte('scheduled_at', start)
          .lte('scheduled_at', end)
      ]);

      if (eventsResponse.error) throw eventsResponse.error;

      if (emailResponse.error) {
          // If table missing, just ignore (might be fresh install)
          if (!emailResponse.error.message.includes('relation "email_campaigns" does not exist')) {
             console.error("Error fetching emails for calendar", emailResponse.error);
          }
      }

      if (flywheelResponse.error) {
          if (!flywheelResponse.error.message.includes('relation "events" does not exist')) {
             console.error("Error fetching flywheel events for calendar", flywheelResponse.error);
          }
      }

      const eventsData = eventsResponse.data;
      const emailData = emailResponse.data;
      const flywheelData = flywheelResponse.data;
      const socialPostsData = socialPostsResponse?.data || [];
      const gcalEvents = gcalResponse || [];

      const emailEvents = (emailData || []).map(e => ({
          id: e.id,
          title: `Email: ${e.name}`,
          description: `Subject: ${e.subject}`,
          type: 'email_campaign',
          event_date: e.scheduled_at,
          status: e.status
      }));

      const flywheelEvents = (flywheelData || []).map(e => ({
          id: e.id,
          title: `Flywheel: ${e.name}`,
          description: `${(e.type || '').replace('_', ' ')}`,
          type: 'flywheel',
          event_date: e.launch_date,
          status: e.status
      }));

      const socialPostEvents = socialPostsData.map(post => ({
          ...post,
          title: `Social: ${post.title}`,
          description: post.content,
          type: 'social_post',
          event_date: post.scheduled_at,
      }));

      setEvents([...(eventsData || []), ...emailEvents, ...flywheelEvents, ...socialPostEvents, ...(gcalEvents || [])]);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));

  // Sort events for list view
  const sortedEventsList = useMemo(() => {
    return [...events].sort((a, b) => {
      const dateA = parseEventDate(a.event_date);
      const dateB = parseEventDate(b.event_date);
      return dateA - dateB;
    });
  }, [events]);

  const gridDays = useMemo(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    const startDayOfWeek = start.getDay(); // 0 = Sun

    // Days from previous month
    const prevMonthDays = [];
    const prevMonthDate = subMonths(currentDate, 1);
    const prevMonthEnd = endOfMonth(prevMonthDate);
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
        prevMonthDays.push({
            date: new Date(prevMonthEnd.getFullYear(), prevMonthEnd.getMonth(), prevMonthEnd.getDate() - i),
            isCurrentMonth: false
        });
    }

    // Current month days
    const currentMonthDays = eachDayOfInterval({ start, end }).map(date => ({
        date,
        isCurrentMonth: true
    }));

    // Days from next month
    const totalCells = 42;
    const nextMonthDays = [];
    const remainingCells = totalCells - prevMonthDays.length - currentMonthDays.length;
    const nextMonthDate = addMonths(currentDate, 1);
    for (let i = 1; i <= remainingCells; i++) {
        nextMonthDays.push({
            date: new Date(nextMonthDate.getFullYear(), nextMonthDate.getMonth(), i),
            isCurrentMonth: false
        });
    }

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  }, [currentDate]);

  // ⚡ Bolt Optimization: Pre-group events by date and type.
  // Why: The calendar grid renders 42 cells, and previously each cell filtered
  // its day's events using an O(N) array filter.
  // Impact: Doing this in the memoized hook reduces the 42 O(N) filters per render
  // down to a single pass, making cell rendering purely O(1) lookups.
  const eventsByDateStr = useMemo(() => {
      const map = {};

      const getDayGroup = (dateStr) => {
          if (!map[dateStr]) {
              map[dateStr] = { all: [] };
          }
          return map[dateStr];
      };

      // Group regular events
      events.forEach(event => {
          const parsedDate = parseEventDate(event.event_date);
          const dateStr = format(parsedDate, 'yyyy-MM-dd');
          const group = getDayGroup(dateStr);

          group.all.push(event);
      });

      // Include holidays for the active years displayed
      if (showHolidays) {
          // It's safest to load the current year of the calendar
          const currentYear = currentDate.getFullYear();
          const holidays = getHolidays(currentYear);

          const addHoliday = (h) => {
              const group = getDayGroup(h.date);
              const holidayEvent = {
                  id: `holiday-${h.date}-${h.title}`,
                  title: h.title,
                  type: h.type,
                  event_date: h.date,
                  isHoliday: true
              };
              group.all.push(holidayEvent);
          };

          holidays.forEach(addHoliday);

          // Also load previous/next year if the calendar shows Dec/Jan respectively
          getHolidays(currentYear - 1).forEach(addHoliday);
          getHolidays(currentYear + 1).forEach(addHoliday);
      }
      return map;
  }, [events, showHolidays, currentDate]);

  const getEventsForDay = useCallback((date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return eventsByDateStr[dateStr] || { all: [] };
  }, [eventsByDateStr]);

  const getEventColor = (type) => {
      switch(type) {
          case 'campaign': return 'bg-blue-50 text-blue-700 border-blue-100';
          case 'content': return 'bg-orange-50 text-orange-700 border-orange-100';
          case 'meeting': return 'bg-charcoal-100 text-charcoal-900 border-charcoal-200';
          case 'promo': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
          case 'holiday': return 'bg-purple-50 text-purple-600 border border-purple-100';
          case 'seasonal': return 'bg-blue-50 text-blue-600 border border-blue-100';
          case 'observance': return 'bg-purple-50 text-purple-600 border border-purple-100';
          case 'email_campaign': return 'bg-purple-50 text-purple-700 border-purple-100';
          case 'google_calendar': return 'bg-blue-50 text-blue-700 border-blue-200';
          case 'flywheel': return 'bg-green-50 text-green-700 border-green-200';
          case 'social_post': return 'bg-indigo-50 text-indigo-700 border-indigo-200';
          default: return 'bg-charcoal-100 text-charcoal-700 border-charcoal-200';
      }
  };

  const handleDayClick = (date) => {
      setSelectedDayDate(date);
      setIsDayModalOpen(true);
  };

  const handleAddEvent = async () => {
      if (!newEvent.title || !newEvent.event_date) return;
      try {
          const { error } = await supabase.from('marketing_events').insert([{
              workspace_id: activeWorkspace.id,
              ...newEvent
          }]);
          if (error) throw error;
          toast.success('Event added successfully');
          setIsAddModalOpen(false);
          setNewEvent({ title: '', event_date: getLocalDateTimeString(), type: 'campaign', description: '', location: '' });
          fetchEvents();
      } catch (err) {
          console.error('Error adding event:', err);
          toast.error('Failed to add event');
      }
  };

  const openEventDetail = (event) => {
      if (event.type === 'social_post') {
          // Revert the "Social: " prefix for editing the title
          const originalTitle = event.title.startsWith('Social: ') ? event.title.substring(8) : event.title;
          setSelectedSocialPost({ ...event, title: originalTitle });
      } else {
          setSelectedEvent(event);
          setNewEvent({
              id: event.id,
              title: event.title || '',
              event_date: event.event_date ? formatForDatetimeLocal(event.event_date) : '',
              type: event.type || 'campaign',
              description: event.description || '',
              location: event.location || ''
          });
          setIsAddModalOpen(true);
      }
  };





  const handleDragStart = (e, event) => {
    e.dataTransfer.setData('application/json', JSON.stringify(event));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetDate) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('application/json');
    if (!data) return;

    const event = JSON.parse(data);
    const newDateStr = format(targetDate, 'yyyy-MM-dd');
    const oldDateStr = format(parseEventDate(event.event_date), 'yyyy-MM-dd');

    if (newDateStr === oldDateStr) return;

    try {
        if (event.type === 'email_campaign') {
            const oldDate = parseEventDate(event.event_date);
            const newDate = new Date(targetDate);
            newDate.setHours(oldDate.getHours(), oldDate.getMinutes(), oldDate.getSeconds());

            const { error } = await supabase.from('email_campaigns').update({
                scheduled_at: newDate.toISOString()
            }).eq('id', event.id);

            if (error) throw error;
            toast.success('Email campaign rescheduled');
        } else if (event.type === 'flywheel') {
             const oldDate = parseEventDate(event.event_date);
             const newDate = new Date(targetDate);
             newDate.setHours(oldDate.getHours(), oldDate.getMinutes(), oldDate.getSeconds());

             const { error } = await supabase.from('events').update({
                  launch_date: newDate.toISOString()
              }).eq('id', event.id);

              if (error) throw error;
              toast.success('Flywheel event rescheduled');
        } else if (event.type === 'social_post') {
             const oldDate = parseEventDate(event.event_date);
             const newDate = new Date(targetDate);
             newDate.setHours(oldDate.getHours(), oldDate.getMinutes(), oldDate.getSeconds());

             const { error } = await supabase.from('social_media_posts').update({
                  scheduled_at: newDate.toISOString()
              }).eq('id', event.id);

              if (error) throw error;
              toast.success('Social post rescheduled');
        } else if (!event.isHoliday) {
            // Update marketing event
             const oldDate = parseEventDate(event.event_date);
             const newDate = new Date(targetDate);
             newDate.setHours(oldDate.getHours(), oldDate.getMinutes(), oldDate.getSeconds());

             const { error } = await supabase.from('marketing_events').update({
                  event_date: newDate.toISOString()
              }).eq('id', event.id);

              if (error) throw error;
              toast.success('Event rescheduled');
        }
        fetchEvents();
    } catch (err) {
        console.error('Error moving event:', err);
        toast.error('Failed to move event');
    }
  };

  return (
    <div className="bg-white dark:bg-charcoal-800 rounded-lg shadow-sm border border-charcoal-200 dark:border-charcoal-700 h-full flex flex-col min-h-[600px] relative">
      {/* Header */}
      <div className="p-4 border-b border-charcoal-200 dark:border-charcoal-700 flex justify-between items-center flex-wrap gap-4">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-charcoal-900 dark:text-charcoal-100">
            {viewMode === 'list' ? 'Upcoming Events' : format(currentDate, 'MMMM yyyy')}
          </h2>
          <div className="flex space-x-1">
            <button onClick={prevMonth} className="p-1 hover:bg-charcoal-100 dark:hover:bg-charcoal-700 rounded text-charcoal-600 dark:text-charcoal-400" aria-label="Previous Month"><SafeIcon icon={FiChevronLeft} /></button>
            <button onClick={nextMonth} className="p-1 hover:bg-charcoal-100 dark:hover:bg-charcoal-700 rounded text-charcoal-600 dark:text-charcoal-400" aria-label="Next Month"><SafeIcon icon={FiChevronRight} /></button>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex bg-charcoal-100 dark:bg-charcoal-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${viewMode === 'month' ? 'bg-white dark:bg-charcoal-600 shadow-sm text-charcoal-900 dark:text-white font-medium' : 'text-charcoal-500 dark:text-charcoal-400 hover:text-charcoal-700 dark:hover:text-charcoal-200'}`}
            >
              Month
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-charcoal-600 shadow-sm text-charcoal-900 dark:text-white font-medium' : 'text-charcoal-500 dark:text-charcoal-400 hover:text-charcoal-700 dark:hover:text-charcoal-200'}`}
            >
              List
            </button>
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center space-x-2 px-3 py-1.5 bg-charcoal-900 dark:bg-charcoal-100 text-white dark:text-charcoal-900 text-sm rounded-lg hover:bg-charcoal-800 dark:hover:bg-charcoal-200 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="text-xs" />
            <span className="hidden sm:inline">Add Event</span>
          </button>
        </div>
      </div>

      {/* Calendar Content */}
      {viewMode === 'list' ? (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {sortedEventsList.length === 0 ? (
            <div className="text-center py-12 text-charcoal-500 dark:text-charcoal-400">
              No upcoming events found.
            </div>
          ) : (
            sortedEventsList.map(event => {
              const isHighlighted = (highlightEventId && event.ids?.includes(highlightEventId)) || event.id === highlightEventId;
              return (
              <div
                key={event.id}
                className={`bg-white dark:bg-charcoal-800 p-4 rounded-lg border border-charcoal-200 dark:border-charcoal-700 shadow-sm hover:shadow-md transition-shadow cursor-pointer flex items-center justify-between ${isHighlighted ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-charcoal-900 animate-[pulse_2s_ease-in-out_infinite]' : ''}`}
                onClick={() => openEventDetail(event)}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${getEventColor(event.type)}`}>
                    <div className="text-center leading-tight">
                      <div className="text-xs opacity-80 uppercase">{format(parseEventDate(event.event_date), 'MMM')}</div>
                      <div className="text-lg font-bold">{format(parseEventDate(event.event_date), 'd')}</div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-charcoal-900 dark:text-charcoal-100">{event.title}</h3>
                    <p className="text-sm text-charcoal-500 dark:text-charcoal-400 line-clamp-1">{event.description || 'No description'}</p>
                    <div className="mt-1 flex items-center space-x-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-charcoal-100 dark:bg-charcoal-700 text-charcoal-600 dark:text-charcoal-300 capitalize">
                        {event.type.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )})
          )}
        </div>
      ) : (
      <div className="flex-1 grid grid-cols-7 grid-rows-6 text-sm">
        {days.map(day => (
          <div key={day} className="p-2 border-b border-charcoal-200 dark:border-charcoal-700 text-center font-medium text-charcoal-500 dark:text-charcoal-400 bg-charcoal-50 dark:bg-charcoal-900">{day}</div>
        ))}

        {gridDays.map(({ date, isCurrentMonth }, idx) => {
            const dayEventsGroup = isCurrentMonth ? getEventsForDay(date) : { all: [] };
            const isPast = isBefore(date, startOfDay(new Date()));

            // ⚡ Bolt Optimization: Use pre-grouped arrays to skip O(N) filtering per render
            const allEvents = dayEventsGroup.all;

            return (
                <div
                    key={idx}
                    onClick={() => isCurrentMonth && handleDayClick(date)}
                    onDragOver={isCurrentMonth ? handleDragOver : undefined}
                    onDrop={isCurrentMonth ? (e) => handleDrop(e, date) : undefined}
                    className={`p-2 border-b border-r dark:border-charcoal-700 min-h-[100px] relative group transition-colors flex flex-col cursor-pointer ${
                        !isCurrentMonth
                            ? 'border-charcoal-100 bg-charcoal-50/50 dark:bg-charcoal-900/50 text-charcoal-400 dark:text-charcoal-600 cursor-default'
                            : isPast
                                ? 'border-charcoal-200 bg-charcoal-100 dark:bg-charcoal-800/50 text-charcoal-500 dark:text-charcoal-500'
                                : 'border-charcoal-100 hover:bg-charcoal-50 dark:hover:bg-charcoal-700/30'
                    }`}
                >
                    <div className="flex justify-between items-start mb-1 shrink-0">
                        <span className={`font-medium ${!isCurrentMonth ? 'text-charcoal-400 dark:text-charcoal-600' : isPast ? 'text-charcoal-500 dark:text-charcoal-500' : 'text-charcoal-900 dark:text-charcoal-100'}`}>{format(date, 'd')}</span>
                        {isCurrentMonth && !isPast && (
                            <button
                                onClick={(e) => { e.stopPropagation(); setNewEvent(prev => ({...prev, event_date: format(date, 'yyyy-MM-dd')})); setIsAddModalOpen(true); }}
                                className="opacity-0 group-hover:opacity-100 text-charcoal-900 dark:text-charcoal-100 hover:text-charcoal-800 dark:hover:text-charcoal-300"
                                aria-label={`Add Event on ${format(date, 'MMMM d')}`}
                            >
                                <SafeIcon icon={FiPlus} className="text-xs" />
                            </button>
                        )}
                        {isCurrentMonth && isPast && (
                             <button
                                onClick={(e) => { e.stopPropagation(); setNewEvent(prev => ({...prev, event_date: format(date, 'yyyy-MM-dd')})); setIsAddModalOpen(true); }}
                                className="opacity-0 group-hover:opacity-100 text-charcoal-400 hover:text-charcoal-600"
                                aria-label={`Add Event on ${format(date, 'MMMM d')}`}
                            >
                                <SafeIcon icon={FiPlus} className="text-xs" />
                            </button>
                        )}
                    </div>

                    <div className="space-y-1 overflow-y-auto flex-1 scrollbar-hide mb-8">
                    {allEvents.map((event) => {
                        const isHighlighted = (highlightEventId && event.ids?.includes(highlightEventId)) || event.id === highlightEventId;
                        return (
                        <div
                            key={event.id}
                            draggable={!event.isHoliday && event.type !== 'google_calendar'}
                            onDragStart={(e) => handleDragStart(e, event)}
                            onClick={(e) => { e.stopPropagation(); openEventDetail(event); }}
                            className={`text-xs rounded truncate cursor-pointer hover:opacity-80 flex items-center p-1 border justify-between ${getEventColor(event.type)} ${isHighlighted ? 'ring-2 ring-indigo-500 animate-[pulse_2s_ease-in-out_infinite]' : ''}`}
                            title={event.title}
                        >
                            <span className="truncate flex-1">{event.title}</span>
                        </div>
                    )})}
                    </div>
                </div>
            );
        })}
      </div>
      )}

      {/* Add Event Modal (Replaced by QuickPostModal) */}
      <QuickPostModal
          isOpen={isAddModalOpen}
          onClose={() => { setIsAddModalOpen(false); fetchEvents(); }}
          initialData={newEvent}
      />

      {/* Edit Social Post Modal */}
      {selectedSocialPost && (
        <EditPostModal
          post={selectedSocialPost}
          onSave={async (id, data) => {
             // In MarketingCalendar, we just update the basic properties for the DB.
             // Full functionality exists in SocialMediaPosts.jsx. For here, a simple update.
             try {
                 const { error } = await supabase.from('social_media_posts').update({
                     title: data.title,
                     content: data.content,
                     scheduled_at: data.scheduled_at,
                     is_event: data.is_event,
                     status: data.status,
                     image: data.image,
                     media_type: data.media_type,
                     initiative_id: data.initiative_id
                 }).eq('id', id);

                 if (error) throw error;
                 toast.success('Social post updated');
                 setSelectedSocialPost(null);
                 fetchEvents();
             } catch (err) {
                 console.error(err);
                 toast.error('Failed to update social post');
             }
          }}
          onCancel={() => setSelectedSocialPost(null)}
          onDelete={async (post) => {
             try {
                 const { error } = await supabase.from('social_media_posts').delete().eq('id', post.id);
                 if (error) throw error;
                 toast.success('Social post deleted');
                 setSelectedSocialPost(null);
                 fetchEvents();
             } catch (err) {
                 console.error(err);
                 toast.error('Failed to delete social post');
             }
          }}
        />
      )}

      {/* Day Detail Modal */}
      {isDayModalOpen && selectedDayDate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
              <div className="bg-white dark:bg-charcoal-800 rounded-lg shadow-xl w-full max-w-md overflow-hidden border border-charcoal-200 dark:border-charcoal-700 max-h-[90vh] flex flex-col">
                  <div className="p-4 border-b border-charcoal-200 dark:border-charcoal-700 flex justify-between items-center bg-charcoal-50 dark:bg-charcoal-900 shrink-0">
                      <h3 className="font-bold text-charcoal-900 dark:text-charcoal-100 flex items-center space-x-2">
                        <SafeIcon icon={FiCalendar} />
                        <span>{format(selectedDayDate, 'MMMM d, yyyy')}</span>
                      </h3>
                      <button onClick={() => setIsDayModalOpen(false)} className="text-charcoal-400 hover:text-charcoal-600 dark:hover:text-charcoal-200" aria-label="Close modal"><SafeIcon icon={FiX} /></button>
                  </div>
                  <div className="p-4 space-y-2 overflow-y-auto">
                      {getEventsForDay(selectedDayDate).all.length === 0 ? (
                          <p className="text-center text-charcoal-500 dark:text-charcoal-400 py-4">No events scheduled for this day.</p>
                      ) : (
                          getEventsForDay(selectedDayDate).all.map((event) => (
                            <div
                                key={event.id}
                                onClick={() => { setIsDayModalOpen(false); openEventDetail(event); }}
                                className={`text-sm rounded p-3 border cursor-pointer hover:opacity-80 transition-colors flex items-center justify-between ${getEventColor(event.type)}`}
                            >
                                <div className="flex items-center space-x-2 overflow-hidden">
                                  <span className="truncate font-medium">{event.title}</span>
                                </div>
                                <SafeIcon icon={FiEdit2} className="w-4 h-4 opacity-50" />
                            </div>
                          ))
                      )}
                  </div>
                  <div className="p-4 border-t border-charcoal-200 dark:border-charcoal-700 bg-charcoal-50 dark:bg-charcoal-900 flex justify-end shrink-0">
                      <button
                        onClick={() => { setNewEvent(prev => ({...prev, event_date: format(selectedDayDate, 'yyyy-MM-dd')})); setIsDayModalOpen(false); setIsAddModalOpen(true); }}
                        className="px-4 py-2 text-sm font-medium text-white bg-charcoal-900 dark:bg-charcoal-100 dark:text-charcoal-900 rounded-md hover:bg-charcoal-800 dark:hover:bg-charcoal-200 flex items-center space-x-1"
                      >
                          <SafeIcon icon={FiPlus} />
                          <span>Add Event</span>
                      </button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default MarketingCalendar;
